import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Table, Button } from 'semantic-ui-react';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import { setRate } from './setRate';
import { roundCurrency } from '../../../util/currency';

const propTypes = {
  productOrder: PropTypes.shape({
    id: PropTypes.number,
    product: PropTypes.shape({
      ProductCode: PropTypes.string,
      ProductId: PropTypes.number,
      ModelT: PropTypes.string,
      Model: PropTypes.string,
      Color: PropTypes.string,
      Voltage: PropTypes.string,
      HDD: PropTypes.string,
      SSD: PropTypes.string,
      USDRetail: PropTypes.number,
      KRWRetail: PropTypes.number,
      ModelId: PropTypes.number,
      AACost: PropTypes.number,
    }).isRequired,
    productModel: PropTypes.shape({
      ModelId: PropTypes.number.isRequired,
      Name: PropTypes.string.isRequired,
      Description: PropTypes.string,
      HS_CODE: PropTypes.string,
    }),
    quantity: PropTypes.number.isRequired,
    discount: PropTypes.number.isRequired,
    customDescription: PropTypes.string,
  }).isRequired,
  currency: PropTypes.string,
  salesOrderType: PropTypes.number.isRequired,
  onCustomDescriptionChange: PropTypes.func,
};

const defaultProps = {};

class ProductOrderListItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      quantity: 0,
      discount: 0,
      fobPrice: 0,
      amount: 0,
      lastAmount: 0,
      isCustomAmount: false,
      customDescription: '',
    };
  }

  numberFormatOnChangeHandler(value, noCurrency) {
    if (typeof value !== 'string') {
      value = String(value);
    }

    let returnValue;
    if (noCurrency) {
      returnValue = value.replace(/,/gi, '');
    } else {
      returnValue =
        value.charAt(0) === '$' || value.charAt(0) === '₩'
          ? value.slice(1).replace(/,/gi, '')
          : value.replace(/,/gi, '');
    }

    return returnValue;
  }

  toIntegerHelper = (num) => {
    if (!num) return 0;

    let valueString = num.toString();
    if (!valueString.includes('.')) return 0;

    let value = valueString.split('.')[1];
    return value ? String(value).length : 0;
  };

  calculateDiscount = (discount) => {
    if (!discount || isNaN(discount)) {
      return 1;
    }

    // 간단하고 정확한 계산: (100 - discount) / 100
    // 예: discount = 40이면 (100 - 40) / 100 = 0.6
    return (100 - discount) / 100;
  };

  setAmount(rate, quantity, discount) {
    if (!rate || !quantity || isNaN(rate) || isNaN(quantity)) {
      return 0;
    }

    let amount = rate * quantity * this.calculateDiscount(discount);

    if (isNaN(amount)) {
      return 0;
    }

    if (this.props.currency.trim() === 'USD') return Number(amount.toFixed(2));
    if (this.props.currency.trim() === 'KRW') return Number(amount.toFixed(0));
    return Number(amount.toFixed(2));
  }

  discountInputOnChangeHandler = (event) => {
    let value = this.numberFormatOnChangeHandler(event.target.value, true);
    this.props.onDiscountChange(this.props.productOrder.id, value);
  };

  quantityInputOnChangeHandler = (event) => {
    let value = this.numberFormatOnChangeHandler(event.target.value, true);
    value = Number(value); // Convert to number
    if (this.props.salesOrderType === 8 && value > 0) {
      value = -value; // Make sure it's negative for salesOrderType 8
    }
    this.props.onQuantityChange(this.props.productOrder.id, value);
  };

  fobPriceInputOnChangeHandler = (event) => {
    let value = this.numberFormatOnChangeHandler(event.target.value, false);
    value = value === '' || isNaN(value) ? 0 : Number(value);

    this.setState({
      fobPrice: value,
      isCustomAmount: true,
    });

    const quantity = this.props.productOrder.quantity || 0;
    const amount = value * quantity;

    this.amountInputOnChangeHandler({
      target: {
        value:
          this.props.currency.trim() === 'USD'
            ? amount.toFixed(2)
            : amount.toFixed(0),
      },
    });
  };

  amountInputOnChangeHandler = (event) => {
    let value = this.numberFormatOnChangeHandler(event.target.value, false);
    value = value === '' || isNaN(value) ? 0 : Number(value);

    this.setState({
      lastAmount: this.state.amount,
      amount: value,
      isCustomAmount: true,
    });

    if (
      this.props.productOrder.quantity === 0 ||
      !this.props.productOrder.quantity
    ) {
      this.props.onDiscountChange(this.props.productOrder.id, 0);
      return;
    }

    const rate = setRate(
      {
        AACost: this.props.productOrder.product.AACost,
        USDRetail: this.props.productOrder.product.USDRetail,
        KRWRetail: this.props.productOrder.product.KRWRetail,
      },
      this.props.currency,
      this.props.company,
      this.props.billingCountry
    );
    const quantity = this.props.productOrder.quantity;
    const discount =
      rate && quantity ? 100 - (value / (rate * quantity)) * 100 : 0;

    this.props.onDiscountChange(this.props.productOrder.id, discount);
  };

  customDescriptionChangeHandler = (event) => {
    const value = event.target.value;
    if (
      value !== null &&
      typeof this.props.onCustomDescriptionChange === 'function'
    ) {
      this.props.onCustomDescriptionChange(this.props.productOrder.id, value);
    }
  };

  componentDidMount() {
    const quantity =
      this.props.salesOrderType === 8
        ? -Math.abs(this.props.productOrder.quantity)
        : this.props.productOrder.quantity;
    const amount = this.setAmount(
      setRate(
        {
          AACost: this.props.productOrder.product.AACost,
          USDRetail: this.props.productOrder.product.USDRetail,
          KRWRetail: this.props.productOrder.product.KRWRetail,
        },
        this.props.currency,
        this.props.company,
        this.props.billingCountry
      ),
      quantity,
      this.props.productOrder.discount
    );
    // Calculate FOB Price with proper rounding to avoid floating point precision errors
    const rawFobPrice = Math.abs(amount / (quantity || 1));
    const fobPrice = roundCurrency(rawFobPrice, this.props.currency);
    this.setState({
      discount: this.props.productOrder.discount,
      rate: setRate(
        {
          AACost: this.props.productOrder.product.AACost,
          USDRetail: this.props.productOrder.product.USDRetail,
          KRWRetail: this.props.productOrder.product.KRWRetail,
        },
        this.props.currency,
        this.props.company,
        this.props.billingCountry
      ),
      quantity,
      amount,
      fobPrice,
      customDescription: this.props.productOrder.customDescription || '',
    });
  }

  componentWillReceiveProps(nextProps) {
    const quantity =
      nextProps.salesOrderType === 8
        ? -Math.abs(nextProps.productOrder.quantity)
        : nextProps.productOrder.quantity;
    const amount = this.setAmount(
      setRate(
        {
          AACost: nextProps.productOrder.product.AACost,
          USDRetail: nextProps.productOrder.product.USDRetail,
          KRWRetail: nextProps.productOrder.product.KRWRetail,
        },
        nextProps.currency,
        nextProps.company,
        nextProps.billingCountry
      ),
      quantity,
      nextProps.productOrder.discount
    );
    // Calculate FOB Price with proper rounding to avoid floating point precision errors
    const rawFobPrice = Math.abs(amount / (quantity || 1));
    const fobPrice = roundCurrency(rawFobPrice, nextProps.currency);
    this.setState({
      discount: nextProps.productOrder.discount,
      quantity,
      rate: setRate(
        {
          AACost: nextProps.productOrder.product.AACost,
          USDRetail: nextProps.productOrder.product.USDRetail,
          KRWRetail: nextProps.productOrder.product.KRWRetail,
        },
        nextProps.currency,
        nextProps.company,
        nextProps.billingCountry
      ),
      fobPrice,
      amount,
      customDescription: nextProps.productOrder.customDescription || '',
    });
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (
      nextState.isCustomAmount &&
      Number(nextState.amount) == Number(nextState.lastAmount) &&
      this.props.salesOrderType === nextProps.salesOrderType &&
      nextState.customDescription === this.state.customDescription
    )
      return false;
    return true;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.salesOrderType !== this.props.salesOrderType) {
      const quantity =
        this.props.salesOrderType === 8
          ? -Math.abs(this.props.productOrder.quantity)
          : Math.abs(this.props.productOrder.quantity);
      const amount = this.setAmount(
        setRate(
          {
            AACost: this.props.productOrder.product.AACost,
            USDRetail: this.props.productOrder.product.USDRetail,
            KRWRetail: this.props.productOrder.product.KRWRetail,
          },
          this.props.currency,
          this.props.company,
          this.props.billingCountry
        ),
        quantity,
        this.props.productOrder.discount
      );
      // Calculate FOB Price with proper rounding to avoid floating point precision errors
      const rawFobPrice = Math.abs(amount / (quantity || 1));
      const fobPrice = roundCurrency(rawFobPrice, this.props.currency);
      this.setState({
        quantity,
        amount,
        fobPrice,
      });
    }
  }

  render() {
    const { productOrder, currency, salesOrderType, company, billingCountry } =
      this.props;
    const { product, productModel, quantity, discount, customDescription } =
      productOrder;

    // 디버깅 로그
    console.log('[ProductOrderListItem] Rate 계산 파라미터:', {
      ProductCode: product.ProductCode,
      AACost: product.AACost,
      USDRetail: product.USDRetail,
      KRWRetail: product.KRWRetail,
      currency,
      company,
      billingCountry,
      SOIID: productOrder.SOIID,
    });

    const rate = setRate(
      {
        AACost: product.AACost,
        USDRetail: product.USDRetail,
        KRWRetail: product.KRWRetail,
      },
      currency,
      company,
      billingCountry
    );

    console.log('[ProductOrderListItem] 계산된 Rate:', {
      ProductCode: product.ProductCode,
      rate,
      SOIID: productOrder.SOIID,
    });
    // Amount를 실시간으로 계산 (setAmount와 동일한 로직 사용)
    const amount = this.setAmount(rate, quantity, discount);
    // FOB Price를 실시간으로 계산: amount / quantity with proper rounding
    const rawFobPrice = Math.abs(amount / (quantity || 1));
    const fobPrice = roundCurrency(rawFobPrice, this.props.currency);
    return (
      <Table.Body>
        <Table.Row>
          <Table.Cell>
            <p style={{ fontWeight: 900 }}>{product.ProductCode}</p>
            {product.Model.trim() == 'CS_Parts' ? (
              <p style={{ margin: 0, fontSize: '12px' }}>
                SKU:{' '}
                {product.sku
                  ? product.sku
                  : `${product.Model.trim()}-${product.Item != null ? product.Item.trim() : null}`}
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: '12px' }}>
                SKU:
                {product.sku
                  ? product.sku
                  : `${product.Model.trim()}-${product.Color.trim()}-${product.Voltage.trim()}-${product.HDD.trim()}-${product.SSD.trim()}`}
              </p>
            )}
            <p style={{ margin: 0, fontSize: '12px' }}>
              {productModel.Description == undefined
                ? ''
                : productModel.Description.trim()}
              {productModel.Description == undefined ||
              productModel.HS_CODE == undefined
                ? ''
                : ' / '}
              {productModel.HS_CODE == undefined
                ? ''
                : productModel.HS_CODE.trim()}
            </p>
            <input
              type="text"
              value={customDescription || ''}
              onChange={this.customDescriptionChangeHandler}
              style={{
                width: 200,
              }}
            />
          </Table.Cell>
          <Table.Cell width={1}>
            <div class="ui input">
              <NumberFormat
                style={{ width: '60px', textAlign: 'right' }}
                value={quantity}
                thousandSeparator={true}
                onChange={this.quantityInputOnChangeHandler}
              />
              {productOrder.serials ? (
                <span
                  style={{
                    marginLeft: '10px',
                    lineHeight: '40px',
                  }}
                >
                  {' '}
                  / {productOrder.serials.length ||
                    productOrder.orderCount}{' '}
                </span>
              ) : null}
            </div>
          </Table.Cell>
          <Table.Cell style={{ textAlign: 'end' }}>
            <NumberFormat
              value={rate}
              displayType={'text'}
              thousandSeparator={true}
              prefix={currency == 'KRW' ? '₩' : '$'}
            />
          </Table.Cell>
          <Table.Cell>
            <div class="ui input">
              <NumberFormat
                style={{ width: '100%', textAlign: 'right' }}
                value={discount}
                thousandSeparator={true}
                onChange={this.discountInputOnChangeHandler}
              />
            </div>
          </Table.Cell>
          <Table.Cell>
            <div class="ui input">
              <NumberFormat
                style={{ width: '100%', textAlign: 'right' }}
                value={fobPrice}
                thousandSeparator={true}
                decimalScale={2}
                prefix={currency == 'KRW' ? '₩' : '$'}
                onChange={this.fobPriceInputOnChangeHandler}
              />
            </div>
          </Table.Cell>
          <Table.Cell>
            <div class="ui input">
              <NumberFormat
                style={{ width: '100%', textAlign: 'right' }}
                value={amount}
                thousandSeparator={true}
                prefix={currency == 'KRW' ? '₩' : '$'}
                onChange={this.amountInputOnChangeHandler}
              />
            </div>
          </Table.Cell>
          <Table.Cell>
            <Button
              style={{ width: '100%', height: '100%' }}
              onClick={() =>
                this.props.onProductOrderDelete(this.props.productOrder.id)
              }
            >
              X
            </Button>
          </Table.Cell>
        </Table.Row>
      </Table.Body>
    );
  }
}

ProductOrderListItem.propTypes = propTypes;
ProductOrderListItem.defaultProps = defaultProps;

export default ProductOrderListItem = connect((state, ownProps) => ({
  selectedCustomer: state.selectedCustomer.billingCustomer,
  // billingCountry는 prop으로 전달되면 그것을 사용하고, 없으면 Redux state에서 가져옴
  billingCountry: ownProps.billingCountry || state.billingCountry,
  company: state.company,
}))(ProductOrderListItem);
